"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";

import SelectInput from "@/components/form/Select/Select";
import { TextArea } from "@/components/form/TextArea";
import { TextInput } from "@/components/form/TextInput";
import { useEffect, useMemo, useState } from "react";

import { publicLeadCaptureFallback } from "@/config/leadCapturePublicFallback.js";
import { normalizeLocation } from "@/config/leadCapture.js";
import If from "@/components/If";
import { Button } from "@/components/actions/Button";
import { Stack } from "@/components/structure/Stack";
import { handleContactForm } from "@/actions";
import PhoneInput from "@/components/form/TextInput/PhoneInput";
import { RadioGroup } from "@/components/form/RadioGroup";
import { useFieldErrors } from "@/components/form/useFieldErrors";

import Script from "next/script";
import { sendGAEvent } from "@next/third-parties/google";

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const initialState = {
  message: "",
};

type PublicLocation = { value: string; label: string; pageHandle?: string };
type PublicInterest = { value: string; label: string; locations: PublicLocation[] };
type PublicLeadCaptureConfig = { interests: PublicInterest[] };

const getPageInterests = (config: PublicLeadCaptureConfig, page: string) =>
  config.interests
    .filter((interest: PublicInterest) =>
      interest.locations.some((location: PublicLocation) => location.pageHandle === page),
    )
    .map((interest) => interest.value);

const getAvailableLocations = (
  config: PublicLeadCaptureConfig,
  interests: string[],
) => {
  const selected = new Map();
  config.interests
    .filter((entry: PublicInterest) => interests.includes(entry.value))
    .flatMap((entry: PublicInterest) => entry.locations)
    .forEach((location: PublicLocation) => selected.set(location.value, location));
  return Array.from(selected.values());
};

export default function Form({ page = "", location = "", theme = "default" }) {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [state, setState] = useState("idle");
  const { errors, validate, clearError } = useFieldErrors();
  const [leadCaptureConfig, setLeadCaptureConfig] = useState<PublicLeadCaptureConfig>(
    publicLeadCaptureFallback,
  );
  const [options, setOptions] = useState<string[]>(
    getPageInterests(publicLeadCaptureFallback, page),
  );
  const [interest, setInterest] = useState(
    (getPageInterests(publicLeadCaptureFallback, page).length === 1 &&
      getPageInterests(publicLeadCaptureFallback, page)[0]) ||
      "",
  );
  const [howDidYouHear, setHowDidYouHear] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(location);

  const locationOptions = useMemo(
    () =>
      getAvailableLocations(
        leadCaptureConfig,
        interest ? [interest] : options,
      ),
    [leadCaptureConfig, interest, options],
  );

  // useEffect(() => {
  //   if (recaptchaLoaded) {
  //     window.grecaptcha.ready(() => {
  //       console.log("reCAPTCHA is ready");
  //     });
  //   }
  // }, [recaptchaLoaded]);

  useEffect(() => {
    const newOptions = getPageInterests(leadCaptureConfig, page);
    setOptions(newOptions);
  }, [page, leadCaptureConfig]);

  useEffect(() => {
    fetch("/api/lead-capture-config", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((config) => {
        if (config?.interests) setLeadCaptureConfig(config);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (options.length === 1) {
      setInterest(options[0]);
      return;
    }

    if (!options.includes(interest)) {
      setInterest("");
    }
  }, [options, interest]);

  useEffect(() => {
    const canonicalLocation = normalizeLocation(location);
    if (
      canonicalLocation &&
      locationOptions.some((item) => item.value === canonicalLocation)
    ) {
      setSelectedLocation(canonicalLocation);
    } else {
      setSelectedLocation("");
    }
  }, [location, locationOptions]);

  // if (!RECAPTCHA_SITE_KEY) {
  //   console.error("reCAPTCHA site key is missing");
  //   return null;
  // }

  // console.log(options, page);

  return (
    <>
      {/* <Script
        src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
        onLoad={() => setRecaptchaLoaded(true)}
        strategy="lazyOnload"
      /> */}
      <form
        noValidate
        onInput={clearError}
        onSubmit={async (ev) => {
          ev.preventDefault();
          setState("loading");
          // if (!recaptchaLoaded) {
          //   console.error("reCAPTCHA not loaded");
          //   return;
          // }

          // const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          //   action: "submit",
          // });
          const formData = new FormData(ev.target);
          const data = Object.fromEntries(formData);
          // data.token = token;
          const requiredFields = [
            "firstName",
            "lastName",
            "email",
            "phone",
            "preferredContact",
            "interests",
            "location",
            // "token",
          ];
          if (
            !validate(ev.currentTarget as HTMLFormElement, data, requiredFields)
          ) {
            setState("idle");
            return;
          }
          const resp = await fetch("/api/contact", {
            method: "POST",
            body: JSON.stringify(data),
          }).catch((err) => {
            console.error(err);
            setState("error");
            return {
              json: () => {},
            };
          });
          const json = await resp.json().catch((err) => {
            console.error(err);
            setState("error");
            return {};
          });

          if (!resp.ok) {
            setState("error");
            return;
          }

          if (json.error || json.missing) {
            setState("error");
            return;
          }

          if (json.success) {
            console.count("LocationDetails:sendGAEvent");
            sendGAEvent("event", "contact_form_submission", {
              value: "success",
            });
            setState("success");
            return;
          }
          // /handleContactForm(ev);
        }}
        className="text-white [--field-error:#fca5a5]"
      >
        <Stack gap="tight" className="text-current">
          <TextInput label="First Name" name="firstName" error={errors.firstName} />
          <TextInput label="Last Name" name="lastName" error={errors.lastName} />
          <PhoneInput label="Phone Number" name="phone" error={errors.phone} required />
          <TextInput label="Email" name="email" error={errors.email} />

          <RadioGroup
            legend="What is your preferred method of contact?"
            name="preferredContact"
            error={errors.preferredContact}
            testId="lead-preferred-contact"
            options={[
              { value: "Phone", label: "Phone" },
              { value: "Email", label: "Email" },
            ]}
            required
          />

          <If condition={theme !== "assisted-living-landing"}>
            <SelectInput
              placeholder="I’m interested in..."
      options={options?.map((interestOption: string) => ({
                value: interestOption,
                label: interestOption,
              }))}
              name="interests"
              error={errors.interests}
              testId="lead-interest"
              value={interest}
              onChange={(value) => setInterest(value)}
            />
          </If>
          <If condition={theme === "assisted-living-landing"}>
            <input type="hidden" name="interests" value="Assisted Living" />
          </If>
          <SelectInput
              placeholder="Location"
              options={locationOptions}
              name="location"
              error={errors.location}
              testId="lead-location"
              value={selectedLocation}
              onChange={(value) => setSelectedLocation(value)}
            />
          {/* <TextInput
            label="Location"
            name="location"
            value={location}
            readOnly
          /> */}
          {/* {#if interests && dataMap[interests]}
						<Select
							name="location"
							label="Location"
							className="mb-8"
							bind:this={formData.location}
							bind:value={values.location}
							required
						>
							{#each dataMap[interests] as location}
								<option>{location}</option>
							{/each}
						</Select>
					{:else}
						<input type="hidden" name="location" value="N/A" />
					{/if} */}

          {/* <If condition={options?.length > 0}>
        <SelectInput
          placeholder="Location"
          options={options?.map((location) => ({
            value: location,
            label: location,
          }))}
          name="location"
          value=""
        />
      </If> */}
          <SelectInput
            placeholder="How did you hear about us?"
            options={[
              { value: "Online Search", label: "Online Search" },
              { value: "Direct Mail", label: "Direct Mail" },
              { value: "Print Ad", label: "Print Ad" },
              { value: "Radio", label: "Radio" },
              {
                value: "Referral (Resident/Family/Friend)",
                label: "Referral (Resident/Family/Friend)",
              },
              {
                value: "Referral (Professional/Healthcare Provider)",
                label: "Referral (Professional/Healthcare Provider)",
              },
              { value: "Social Media", label: "Social Media" },
              { value: "Television", label: "Television" },
              { value: "Other", label: "Other" },
            ]}
            name="howDidYouHear"
            value={howDidYouHear}
            onChange={(value) => setHowDidYouHear(value)}
          />
          <If condition={howDidYouHear === "Other"}>
            <TextInput
              label="Other (Please Specify)"
              name="howDidYouHearOther"
            />
          </If>
          <TextArea
            label="Message"
            name="comments"
            error={errors.comments}
            placeholder="Enter your message here"
          />

          <label htmlFor="remark-reservation" className="remark">
            Remark
          </label>
          <input
            className="remark"
            name="remark"
            id="remark-reservation"
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{(2, 4)}$"
            placeholder="name@domain.com"
          />

          <If condition={state === "error"}>
            <p className="text-red-500">
              There was an error submitting the form
            </p>
          </If>

          <If condition={state === "success"}>
            <p data-testid="lead-submit-success" className="text-green-500 text-center">
              Form submitted successfully
            </p>
            <button
              onClick={() => setState("idle")}
              className="px-0 text-center text-orange underline"
            >
              Send Another Contact Form
            </button>
          </If>

          {/* <p aria-live="polite">{state?.message}</p> */}
          <If condition={state !== "success"}>
            <Button
              type="submit"
              className="w-max"
              disabled={state === "loading"}
            >
              <If condition={state === "loading"}>Sending...</If>
              <If condition={state !== "loading"}>Send Request</If>
            </Button>
          </If>

          {/* <small className="text-[#929292] text-sm">
            This site is protected by reCAPTCHA and the Google{" "}
            <a
              className="text-[#929292] underline text-sm"
              href="https://policies.google.com/privacy"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              className="text-[#929292] underline text-sm"
              href="https://policies.google.com/terms"
            >
              Terms of Service
            </a>{" "}
            apply.
          </small> */}
        </Stack>
      </form>
    </>
  );
}
